import React from 'react';

import type { EntryPoint } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ProjectIcon from '@atlaskit/icon/core/project';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	PanelActionCloseSmart,
	PanelActionGroup,
	PanelBody,
	PanelContainer,
	PanelFooter,
	PanelHeader,
	PanelTitle,
	usePanel,
} from '@atlassian/panel-system';
import { PanelContent } from '@atlassian/panel-system/panel-content';

// Helper to get entry point name for display
function getEntryPointName(entryPoint: EntryPoint<any, any>): string {
	const root = entryPoint.root as any;
	return root?.getModuleName?.() || root?.getModuleId?.() || 'unknown-entrypoint';
}

const styles = cssMap({
	stateDisplay: {
		paddingBlockStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		backgroundColor: token('color.background.neutral'),
	},
	dummyContent: {
		width: '100%',
		height: '300px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
});

export const ExamplePanel = (): React.JSX.Element => {
	const { panel } = usePanel();
	const entryPointName =
		'entryPoint' in panel ? getEntryPointName(panel.entryPoint) : 'no-entrypoint';

	return (
		<PanelContainer testId={`panel-${panel.instanceId}`}>
			<PanelHeader>
				<PanelTitle icon={<ProjectIcon label="Panel" />}>
					{entryPointName} - {panel.instanceId}
				</PanelTitle>
				<PanelActionGroup>
					<PanelActionCloseSmart testId={`close-${panel.instanceId}`} />
				</PanelActionGroup>
			</PanelHeader>
			<PanelContent>
				<PanelBody>
					<Stack space="space.200">
						<Heading size="small" as="h3">
							Panel Content
						</Heading>
						{panel.params && Object.keys(panel.params).length > 0 && (
							<>
								<Heading size="xsmall" as="h4">
									Params:
								</Heading>
								<Box xcss={styles.stateDisplay}>
									<pre>{JSON.stringify(panel.params, null, 2)}</pre>
								</Box>
							</>
						)}
						<Box xcss={styles.dummyContent}></Box>
						<Box xcss={styles.dummyContent}></Box>
						<Box xcss={styles.dummyContent}></Box>
						<Box xcss={styles.dummyContent}></Box>
					</Stack>
				</PanelBody>
			</PanelContent>
			<PanelFooter>Footer</PanelFooter>
		</PanelContainer>
	);
};
